/**
 * Service centralisé pour la gestion sécurisée des sessions
 *
 * Ce service garantit que :
 * - Tous les tokens sont toujours chiffrés avant stockage
 * - Tous les tokens sont déchiffrés lors de la lecture
 * - Le nettoyage est complet lors du logout
 */
import { LocalStorage } from "quasar";
import { encryptToken, decryptToken } from "../utils/tokenCrypto.js";

class SessionStorageService {
  // Clés de stockage
  static KEYS = {
    TOKEN: "ykT",
    REFRESH_TOKEN: "ykrefreshT",
    USER: "ykSessionUser",
    OTHER: "codrix", // À vérifier si nécessaire
  };

  /**
   * Sauvegarde le token de manière sécurisée (toujours chiffré)
   * @param {string} token - Token à sauvegarder (en clair)
   */
  static setToken(token) {
    if (!token) {

      return;
    }
    try {
      const encryptedToken = encryptToken(token);
      LocalStorage.setItem(this.KEYS.TOKEN, encryptedToken);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Récupère et déchiffre le token
   * @returns {string|null} Token déchiffré ou null si absent
   */
  static getToken() {
    const encryptedToken = LocalStorage.getItem(this.KEYS.TOKEN);
    if (!encryptedToken) return null;

    try {
      return decryptToken(encryptedToken);
    } catch (error) {

      // Nettoyer le token corrompu
      this.removeToken();
      return null;
    }
  }

  /**
   * Vérifie si un token existe (sans le déchiffrer)
   * @returns {boolean}
   */
  static hasToken() {
    return LocalStorage.hasItem(this.KEYS.TOKEN);
  }

  /**
   * Supprime le token
   */
  static removeToken() {
    LocalStorage.removeItem(this.KEYS.TOKEN);
  }

  /**
   * Sauvegarde les données utilisateur
   * @param {Object} userData - Données utilisateur à sauvegarder
   */
  static setUser(userData) {
    if (!userData) {

      return;
    }
    try {
      LocalStorage.setItem(this.KEYS.USER, JSON.stringify(userData));
    } catch (error) {

      throw error;
    }
  }

  /**
   * Récupère les données utilisateur
   * @returns {Object|null} Données utilisateur ou null si absent
   */
  static getUser() {
    const userData = LocalStorage.getItem(this.KEYS.USER);
    if (!userData) return null;

    try {
      return JSON.parse(userData);
    } catch (error) {

      this.removeUser();
      return null;
    }
  }

  /**
   * Vérifie si un utilisateur est stocké
   * @returns {boolean}
   */
  static hasUser() {
    return LocalStorage.hasItem(this.KEYS.USER);
  }

  /**
   * Supprime les données utilisateur
   */
  static removeUser() {
    LocalStorage.removeItem(this.KEYS.USER);
  }

  /**
   * Sauvegarde le refresh token
   * @param {string} refreshToken - Refresh token à sauvegarder
   */
  static setRefreshToken(refreshToken) {
    if (!refreshToken) {

      return;
    }
    try {
      LocalStorage.setItem(this.KEYS.REFRESH_TOKEN, refreshToken);
    } catch (error) {

      throw error;
    }
  }

  /**
   * Récupère le refresh token
   * @returns {string|null} Refresh token ou null si absent
   */
  static getRefreshToken() {
    return LocalStorage.getItem(this.KEYS.REFRESH_TOKEN);
  }

  /**
   * Supprime le refresh token
   */
  static removeRefreshToken() {
    LocalStorage.removeItem(this.KEYS.REFRESH_TOKEN);
  }

  /**
   * Nettoie TOUTES les données de session
   * À appeler lors du logout
   */
  static clearAll() {
    this.removeToken();
    this.removeRefreshToken();
    this.removeUser();
    LocalStorage.removeItem(this.KEYS.OTHER);

    // Nettoyer aussi les flags d'authentification sociale
    LocalStorage.removeItem("appleAuth");
    LocalStorage.removeItem("googleAuth");
    LocalStorage.removeItem("useCookieAuth");
  }

  /**
   * Vérifie si une session existe
   * @returns {boolean}
   */
  static hasSession() {
    return this.hasToken() && this.hasUser();
  }
}

export default SessionStorageService;
